import React from 'react';
import { useNavigate } from 'react-router-dom';
import { usePageMeta } from '../hooks/usePageMeta';

const HEADER_IMG = '/bracketsim_header.png';

const Contact: React.FC = () => {
  const navigate = useNavigate();
  usePageMeta({
    title: 'Contact — My Bracket Sim',
    description: 'Get in touch with the My Bracket Sim team.',
    path: '/contact',
  });

  return (
    <div style={s.page}>
      {/* Header */}
      <header style={s.header}>
        <img
          src={HEADER_IMG}
          alt="BracketSim"
          style={s.logo}
          onClick={() => navigate('/')}
        />
        <nav style={s.nav}>
          <a href="/how-it-works" style={s.navLink}>How It Works</a>
          <a href="/pricing" style={s.navLink}>Pricing</a>
          <a href="/login" className="ct-login-link" style={s.navAccent}>Log In</a>
        </nav>
      </header>

      {/* Content */}
      <section style={s.main}>
        <div style={s.inner}>
          <span style={s.label}>{'{ '}CONTACT{' }'}</span>
          <h1 style={s.title}>Get in touch.</h1>

          <div style={s.emailBlock}>
            <a href="mailto:hello@mybracketsim.com" className="ct-email" style={s.emailLink}>
              hello@mybracketsim.com
            </a>
          </div>

          <p style={s.text}>
            Have a question, feature request, or just want to say hi? We typically
            respond within 24 hours. Whether it's help with importing your pool,
            understanding your simulation results, or a suggestion for how we can
            improve — we're happy to help.
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer style={s.footer}>
        <p style={s.footerRow}>
          <a href="/privacy" className="ct-flink">Privacy Policy</a>
          <span style={s.dot}>&middot;</span>
          <a href="/contact" className="ct-flink">Contact</a>
        </p>
        <p style={{ marginBottom: '12px' }}>
          <a href="mailto:hello@mybracketsim.com" className="ct-flink">
            hello@mybracketsim.com
          </a>
        </p>
        <p style={s.copy}>&copy; 2026 MyBracketSim</p>
      </footer>
    </div>
  );
};

const s: Record<string, React.CSSProperties> = {
  page: {
    minHeight: '100vh',
    backgroundColor: '#faf8f5',
    fontFamily: '"Outfit", sans-serif',
    color: '#1c1917',
    display: 'flex',
    flexDirection: 'column',
  },

  /* Header */
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '18px 8%',
    borderBottom: '1px solid #e7e5e4',
  },
  logo: { height: '36px', cursor: 'pointer' },
  nav: { display: 'flex', gap: '24px', alignItems: 'center' },
  navLink: {
    fontFamily: '"Space Mono", monospace',
    fontSize: '0.85rem',
    color: '#1c1917',
    textDecoration: 'none',
    letterSpacing: '0.03em',
  },
  navAccent: {
    fontFamily: '"Space Mono", monospace',
    fontSize: '0.85rem',
    color: '#e8622d',
    textDecoration: 'none',
    letterSpacing: '0.03em',
    padding: '7px 18px',
    border: '1.5px solid #e8622d',
    borderRadius: '3px',
  },

  /* Main content */
  main: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '80px 8%',
  },
  inner: {
    maxWidth: '540px',
    width: '100%',
  },
  label: {
    fontFamily: '"Space Mono", monospace',
    fontSize: '0.75rem',
    letterSpacing: '0.18em',
    color: '#e8622d',
    display: 'block',
    marginBottom: '20px',
  },
  title: {
    fontFamily: '"Space Mono", monospace',
    fontSize: 'clamp(1.5rem, 3.2vw, 2.2rem)',
    fontWeight: 700,
    lineHeight: 1.4,
    color: '#1c1917',
    margin: '0 0 32px',
  },
  emailBlock: {
    marginBottom: '32px',
    paddingBottom: '32px',
    borderBottom: '1px solid #e7e5e4',
  },
  emailLink: {
    fontFamily: '"Space Mono", monospace',
    fontSize: 'clamp(1.1rem, 2.5vw, 1.5rem)',
    fontWeight: 700,
    color: '#e8622d',
    textDecoration: 'none',
  },
  text: {
    fontSize: '1rem',
    color: '#57534e',
    lineHeight: 1.8,
    margin: 0,
  },

  /* Footer */
  footer: {
    padding: '36px 8%',
    textAlign: 'center',
    borderTop: '1px solid #e7e5e4',
  },
  footerRow: { marginBottom: '10px' },
  dot: { margin: '0 10px', color: '#a8a29e' },
  copy: { color: '#a8a29e', fontSize: '0.8rem', margin: 0 },
};

/* Load fonts */
if (!document.querySelector('#bracketsim-fonts')) {
  const link = document.createElement('link');
  link.id = 'bracketsim-fonts';
  link.rel = 'stylesheet';
  link.href =
    'https://fonts.googleapis.com/css2?family=Outfit:wght@400;500;600;700&family=Space+Mono:wght@400;700&display=swap';
  document.head.appendChild(link);
}

/* Interaction styles */
if (!document.querySelector('#contact-styles')) {
  const sheet = document.createElement('style');
  sheet.id = 'contact-styles';
  sheet.textContent = `
    .ct-login-link:hover {
      background-color: #e8622d !important;
      color: #fff !important;
    }
    .ct-email:hover { text-decoration: underline; }
    .ct-flink {
      color: #78716c;
      text-decoration: none;
      font-size: 0.9rem;
    }
    .ct-flink:hover { color: #e8622d; }
  `;
  document.head.appendChild(sheet);
}

export default Contact;
